// src/components/expenses/ExpenseForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useExpenses } from '../../hooks/useExpenses';
import { useProjects } from '../../hooks/useProjects';
import { useCategories } from '../../hooks/useCategories';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { expenseApi } from '../../api/expenseApi';
import PhotoUpload from '../common/PhotoUpload';
import {
    ArrowLeftIcon, CheckIcon, PaperClipIcon,
    DocumentIcon, XMarkIcon, CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/formatters';
import axiosConfig from '../../api/axiosConfig';
import { useTranslation } from 'react-i18next';

const PAYMENT_METHODS = [
    { value: 'CASH',         label: '💵 Contanti' },
    { value: 'CARD',         label: '💳 Carta di Credito' },
    { value: 'WAVE',         label: '🌊 Wave Mobile Money' },
    { value: 'ORANGE_MONEY', label: '🟠 Orange Money' },
    { value: 'MTN_MONEY',    label: '🟡 MTN Mobile Money' },
];

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_SIZE_MB = 10;

const Field = ({ label, required, error, children, hint }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {label} {required && <span className="text-red-400">*</span>}
        </label>
        {children}
        {hint && !error && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
);

const inputClass = (error) =>
    `w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-gray-50 ${error ? 'border-red-400 bg-red-50' : 'border-gray-200'
    }`;

const ExpenseForm = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const { organization } = useAuth();
    const currency = organization?.currency || 'GNF';

    const { createExpense, updateExpense } = useExpenses();
    const { projects, loading: projectsLoading } = useProjects({ limit: 100 });
    const { expenseCategories } = useCategories();

    const [loading, setLoading] = useState(false);
    const [loadingExpense, setLoadingExpense] = useState(isEdit);
    const [errors, setErrors] = useState({});
    const [uploading, setUploading] = useState(false);

    // Upload fattura (documento PDF/immagine)
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [invoicePreview, setInvoicePreview] = useState(null);
    const [existingInvoiceUrl, setExistingInvoiceUrl] = useState(null);
    const fileInputRef = useRef(null);

    const [form, setForm] = useState({
        reference: '',
        description: '',
        category: 'MATERIALS',
        amount: '',
        taxRate: '0',
        supplierName: '',
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        paymentDate: '',
        paymentMethod: 'CASH',
        projectId: '',
        notes: '',
        photos: [],  // ✅ array foto cantiere
    });

    useEffect(() => { if (isEdit) loadExpense(); }, [id]);

    const loadExpense = async () => {
        try {
            setLoadingExpense(true);
            const response = await expenseApi.getById(id);
            const e = response.data;
            setForm({
                reference: e.reference || '',
                description: e.description || '',
                category: e.category || 'MATERIALS',
                amount: e.amount?.toString() || '',
                taxRate: e.taxRate?.toString() || '0',
                supplierName: e.supplierName || '',
                invoiceNumber: e.invoiceNumber || '',
                invoiceDate: e.invoiceDate?.split('T')[0] || '',
                paymentDate: e.paymentDate?.split('T')[0] || '',
                paymentMethod: e.paymentMethod || 'CASH',
                projectId: e.projectId || '',
                notes: e.notes || '',
                photos: e.photos || [],  // ✅ foto esistenti
            });
            if (e.invoiceUrl) setExistingInvoiceUrl(e.invoiceUrl);
        } catch {
            toast.error('Errore nel caricamento della spesa');
            navigate('/expenses');
        } finally {
            setLoadingExpense(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // ==================== FILE FATTURA ====================
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.error('Formato non supportato. Usa PDF, JPG, PNG o WebP');
            return;
        }
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            toast.error(`File troppo grande. Massimo ${MAX_SIZE_MB}MB`);
            return;
        }
        setInvoiceFile(file);
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (ev) => setInvoicePreview(ev.target.result);
            reader.readAsDataURL(file);
        } else {
            setInvoicePreview('pdf');
        }
    };

    const handleRemoveFile = () => {
        setInvoiceFile(null);
        setInvoicePreview(null);
        setExistingInvoiceUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const uploadInvoice = async (expenseId) => {
        if (!invoiceFile) return null;
        const formData = new FormData();
        formData.append('file', invoiceFile);
        formData.append('expenseId', expenseId);
        try {
            setUploading(true);
            const response = await axiosConfig.post('/documents/upload-invoice', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data.data?.url || null;
        } catch {
            toast.error('Errore nel caricamento della fattura — la spesa è stata salvata');
            return null;
        } finally {
            setUploading(false);
        }
    };

    // ==================== CALCOLI ====================
    const amount = parseFloat(form.amount) || 0;
    const taxRate = parseFloat(form.taxRate) || 0;
    const taxAmount = amount * taxRate / 100;
    const total = amount + taxAmount;

    // ==================== VALIDAZIONE ====================
    const validate = () => {
        const e = {};
        if (!form.reference.trim()) e.reference = 'Riferimento richiesto';
        if (!form.description.trim()) e.description = 'Descrizione richiesta';
        if (!form.projectId) e.projectId = 'Progetto richiesto';
        if (!form.amount || parseFloat(form.amount) <= 0) e.amount = 'Importo deve essere > 0';
        if (!form.invoiceDate) e.invoiceDate = 'Data fattura richiesta';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // ==================== SUBMIT ====================
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);

        const data = {
            ...form,
            amount: parseFloat(form.amount),
            taxRate: parseFloat(form.taxRate),
            paymentDate: form.paymentDate || null,
        };

        const result = isEdit ? await updateExpense(id, data) : await createExpense(data);

        if (result.success) {
            if (invoiceFile) await uploadInvoice(result.data?.id || id);
            navigate('/expenses');
        } else {
            setErrors({ general: result.error });
        }
        setLoading(false);
    };

    if (loadingExpense || projectsLoading) return <LoadingSpinner />;

    return (
        <div className="max-w-3xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex items-center gap-3">
                <button onClick={() => navigate('/expenses')}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition">
                    <ArrowLeftIcon className="w-5 h-5" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isEdit ? 'Modifica Spesa' : 'Nuova Spesa'}
                    </h1>
                    <p className="text-gray-500 text-sm mt-0.5">
                        {isEdit ? 'Aggiorna i dati della spesa' : 'Registra una nuova spesa con fattura allegata'}
                    </p>
                </div>
            </div>

            {errors.general && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    ⚠️ {errors.general}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>

                {/* Info base */}
                <div className="bg-white rounded-2xl border p-6 space-y-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Informazioni Base</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Riferimento" required error={errors.reference} hint="Es. INV-2025-001">
                            <input type="text" name="reference" value={form.reference} onChange={handleChange}
                                className={inputClass(errors.reference)} placeholder="INV-2025-001" />
                        </Field>
                        <Field label="Progetto" required error={errors.projectId}>
                            <select name="projectId" value={form.projectId} onChange={handleChange} className={inputClass(errors.projectId)}>
                                <option value="">— Seleziona progetto —</option>
                                {projects.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Descrizione" required error={errors.description}>
                        <textarea name="description" value={form.description} onChange={handleChange} rows={3}
                            className={inputClass(errors.description)} placeholder="Descrizione dettagliata della spesa..." />
                    </Field>
                </div>

                {/* Importo */}
                <div className="bg-white rounded-2xl border p-6 space-y-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">💰 Importo e Categoria</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Categoria" required>
                            <select name="category" value={form.category} onChange={handleChange} className={inputClass(false)}>
                                {expenseCategories.map(cat => <option key={cat.name} value={cat.name}>{cat.label}</option>)}
                            </select>
                        </Field>
                        <Field label={`Importo (${currency})`} required error={errors.amount}>
                            <input type="number" name="amount" value={form.amount} onChange={handleChange}
                                min="1" step="1000" className={inputClass(errors.amount)} placeholder="0" />
                        </Field>
                        <Field label="IVA / Tasse (%)" hint="0% per esenzioni">
                            <input type="number" name="taxRate" value={form.taxRate} onChange={handleChange}
                                min="0" max="100" step="1" className={inputClass(false)} />
                        </Field>
                    </div>
                    {amount > 0 && (
                        <div className="bg-blue-50 rounded-xl p-4">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Imponibile</span>
                                <span className="font-medium text-gray-900">{formatCurrency(amount, currency)}</span>
                            </div>
                            {taxRate > 0 && (
                                <div className="flex justify-between text-sm mt-1">
                                    <span className="text-gray-500">IVA {taxRate}%</span>
                                    <span className="font-medium text-gray-700">+ {formatCurrency(taxAmount, currency)}</span>
                                </div>
                            )}
                            <div className="border-t border-blue-100 mt-2 pt-2 flex justify-between">
                                <span className="text-sm font-semibold text-blue-900">Totale</span>
                                <span className="text-xl font-bold text-blue-700">{formatCurrency(total, currency)}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Fornitore */}
                <div className="bg-white rounded-2xl border p-6 space-y-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">🏭 Fornitore</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Nome Fornitore">
                            <input type="text" name="supplierName" value={form.supplierName} onChange={handleChange}
                                className={inputClass(false)} placeholder="Es. Ciments de Guinée SA" />
                        </Field>
                        <Field label="Numero Fattura">
                            <input type="text" name="invoiceNumber" value={form.invoiceNumber} onChange={handleChange}
                                className={inputClass(false)} placeholder="Es. F2025-001" />
                        </Field>
                    </div>
                </div>

                {/* Date */}
                <div className="bg-white rounded-2xl border p-6 space-y-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">📅 Date e Pagamento</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Data Fattura" required error={errors.invoiceDate}>
                            <input type="date" name="invoiceDate" value={form.invoiceDate} onChange={handleChange}
                                className={inputClass(errors.invoiceDate)} />
                        </Field>
                        <Field label="Data Pagamento" hint="Lascia vuoto se non pagato">
                            <input type="date" name="paymentDate" value={form.paymentDate} onChange={handleChange}
                                className={inputClass(false)} />
                        </Field>
                        <Field label="Metodo Pagamento">
                            <select name="paymentMethod" value={form.paymentMethod} onChange={handleChange} className={inputClass(false)}>
                                {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </Field>
                    </div>
                </div>

                {/* Upload fattura */}
                <div className="bg-white rounded-2xl border p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <PaperClipIcon className="w-5 h-5 text-gray-400" />
                        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Copia Fattura</h2>
                        <span className="text-xs text-gray-400">(opzionale)</span>
                    </div>

                    {existingInvoiceUrl && !invoiceFile && (
                        <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                            <DocumentIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-emerald-800">Fattura allegata</p>
                                <a href={existingInvoiceUrl} target="_blank" rel="noreferrer"
                                    className="text-xs text-emerald-600 hover:underline truncate block">
                                    Visualizza fattura →
                                </a>
                            </div>
                            <button type="button" onClick={handleRemoveFile}
                                className="p-1 text-emerald-400 hover:text-red-500 transition">
                                <XMarkIcon className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {invoiceFile && (
                        <div className="relative rounded-xl overflow-hidden border border-gray-200">
                            {invoicePreview === 'pdf' ? (
                                <div className="flex items-center gap-3 p-4 bg-gray-50">
                                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-xl">📄</div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-900">{invoiceFile.name}</p>
                                        <p className="text-xs text-gray-400">{(invoiceFile.size / 1024).toFixed(0)} KB · PDF</p>
                                    </div>
                                    <button type="button" onClick={handleRemoveFile}
                                        className="p-1.5 hover:bg-gray-200 rounded-lg transition">
                                        <XMarkIcon className="w-4 h-4 text-gray-500" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative">
                                    <img src={invoicePreview} alt="Preview fattura" className="w-full max-h-48 object-contain bg-gray-50" />
                                    <button type="button" onClick={handleRemoveFile}
                                        className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-red-50 text-gray-500 hover:text-red-600 rounded-lg shadow transition">
                                        <XMarkIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {!invoiceFile && !existingInvoiceUrl && (
                        <div onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition group">
                            <CloudArrowUpIcon className="w-10 h-10 text-gray-300 group-hover:text-blue-400 mx-auto mb-3 transition" />
                            <p className="text-sm font-medium text-gray-600 group-hover:text-blue-600">
                                Clicca per allegare la fattura
                            </p>
                            <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, WebP · Max {MAX_SIZE_MB}MB</p>
                        </div>
                    )}

                    <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={handleFileSelect} className="hidden" />
                </div>

                {/* ✅ FOTO CANTIERE — solo in modalità modifica (dopo creazione) */}
                {isEdit && (
                    <div className="bg-white rounded-2xl border p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-lg">📷</span>
                            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Foto Cantiere</h2>
                            <span className="text-xs text-gray-400">(max 5 foto)</span>
                        </div>
                        <PhotoUpload
                            entityType="expenses"
                            entityId={id}
                            existingPhotos={form.photos || []}
                            onPhotosChange={(photos) => setForm(prev => ({ ...prev, photos }))}
                        />
                    </div>
                )}

                {/* Note */}
                <div className="bg-white rounded-2xl border p-6">
                    <Field label="Note aggiuntive">
                        <textarea name="notes" value={form.notes} onChange={handleChange} rows={2}
                            className={inputClass(false)} placeholder="Note interne, riferimenti, ecc..." />
                    </Field>
                </div>

                {/* Azioni */}
                <div className="flex gap-3 justify-end pb-6">
                    <button type="button" onClick={() => navigate('/expenses')}
                        className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium">
                        Annulla
                    </button>
                    <button type="submit" disabled={loading || uploading}
                        className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium disabled:opacity-50">
                        {loading || uploading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                {uploading ? 'Caricamento...' : 'Salvataggio...'}
                            </>
                        ) : (
                            <><CheckIcon className="w-4 h-4" />{isEdit ? 'Aggiorna Spesa' : 'Crea Spesa'}</>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ExpenseForm;